/**
 * Food Database Service
 *
 * Integrates with Nutritionix API (https://www.nutritionix.com/business/api)
 * for comprehensive food database with barcode scanning capability.
 *
 * Features:
 * - Natural language food search
 * - Barcode/UPC lookup
 * - Branded and common foods
 * - Detailed nutritional information
 * - Exercise calorie calculations
 *
 * Phase 3 - Advanced Integration
 */

import { HTTP } from "./http";

let API_SERVER = "";
if (window.location.href.indexOf("8080") > -1) {
  API_SERVER = "http://www.localhost:8888/back-end-endpoint-food-database.php";
} else {
  API_SERVER = "/personal-training-BE/back-end-endpoint-food-database.php";
}

const FoodDatabase = {
  // =========================================================================
  // FOOD SEARCH
  // =========================================================================

  /**
   * Search for foods using natural language
   * @param {string} query - Natural language query (e.g., "1 cup of rice" or "Big Mac")
   * @param {Object} options - Search options
   * @param {boolean} options.detailed - Get detailed nutrition info
   * @param {boolean} options.branded - Include branded foods
   * @param {boolean} options.common - Include common foods
   */
  searchFoods(query, options = {}) {
    return HTTP.post(API_SERVER, {
      action: "searchFoods",
      query: query,
      detailed: options.detailed !== false,
      branded: options.branded !== false,
      common: options.common !== false,
    });
  },

  /**
   * Get instant food suggestions for autocomplete
   * @param {string} query - Partial search query
   * @param {boolean} branded - Include branded foods
   * @param {boolean} common - Include common foods
   */
  instantSearch(query, branded = true, common = true) {
    return HTTP.post(API_SERVER, {
      action: "instantSearch",
      query: query,
      branded: branded,
      common: common,
    });
  },

  /**
   * Get detailed nutrition for a specific food item
   * @param {string} foodId - Food ID or name
   * @param {string} foodType - 'common' or 'branded'
   */
  getFoodDetails(foodId, foodType = "common") {
    return HTTP.post(API_SERVER, {
      action: "getFoodDetails",
      food_id: foodId,
      food_type: foodType,
    });
  },

  // =========================================================================
  // BARCODE SCANNING
  // =========================================================================

  /**
   * Look up food by barcode/UPC
   * @param {string} barcode - UPC/EAN barcode number
   */
  lookupBarcode(barcode) {
    return HTTP.post(API_SERVER, {
      action: "lookupBarcode",
      barcode: barcode,
    });
  },

  /**
   * Get recently scanned barcodes for a user
   * @param {number} userId - User ID
   * @param {number} limit - Number of results
   */
  getRecentScans(userId, limit = 10) {
    return HTTP.post(API_SERVER, {
      action: "getRecentScans",
      user_id: userId,
      limit: limit,
    });
  },

  /**
   * Save a scanned food to user's history
   * @param {number} userId - User ID
   * @param {Object} foodData - Food data from barcode lookup
   */
  saveScanHistory(userId, foodData) {
    return HTTP.post(API_SERVER, {
      action: "saveScanHistory",
      user_id: userId,
      food_data: foodData,
    });
  },

  // =========================================================================
  // BRANDED FOODS
  // =========================================================================

  /**
   * Search branded foods only
   * @param {string} query - Search query
   * @param {string} brandId - Specific brand ID (optional)
   */
  searchBranded(query, brandId = null) {
    return HTTP.post(API_SERVER, {
      action: "searchBranded",
      query: query,
      brand_id: brandId,
    });
  },

  /**
   * Get branded food by nix_item_id
   * @param {string} nixItemId - Nutritionix item ID
   */
  getBrandedFood(nixItemId) {
    return HTTP.post(API_SERVER, {
      action: "getBrandedFood",
      nix_item_id: nixItemId,
    });
  },

  // =========================================================================
  // EXERCISE CALORIES
  // =========================================================================

  /**
   * Calculate calories burned from exercise description
   * @param {string} query - Natural language exercise (e.g., "ran 5km")
   * @param {Object} userInfo - User info for calculation
   * @param {number} userInfo.weight_kg - Weight in kg
   * @param {number} userInfo.height_cm - Height in cm
   * @param {number} userInfo.age - Age in years
   * @param {string} userInfo.gender - 'male' or 'female'
   */
  calculateExerciseCalories(query, userInfo = {}) {
    return HTTP.post(API_SERVER, {
      action: "calculateExerciseCalories",
      query: query,
      weight_kg: userInfo.weight_kg || 70,
      height_cm: userInfo.height_cm || 175,
      age: userInfo.age || 25,
      gender: userInfo.gender || "male",
    });
  },

  // =========================================================================
  // FAVORITES & CUSTOM FOODS
  // =========================================================================

  /**
   * Add a food to user's favorites
   * @param {number} userId - User ID
   * @param {Object} foodData - Food data to save
   */
  addToFavorites(userId, foodData) {
    return HTTP.post(API_SERVER, {
      action: "addToFavorites",
      user_id: userId,
      food_data: foodData,
    });
  },

  /**
   * Get user's favorite foods
   * @param {number} userId - User ID
   */
  getFavorites(userId) {
    return HTTP.post(API_SERVER, {
      action: "getFavorites",
      user_id: userId,
    });
  },

  /**
   * Remove food from favorites
   * @param {number} userId - User ID
   * @param {number} favoriteId - Favorite entry ID
   */
  removeFromFavorites(userId, favoriteId) {
    return HTTP.post(API_SERVER, {
      action: "removeFromFavorites",
      user_id: userId,
      favorite_id: favoriteId,
    });
  },

  /**
   * Create a custom food entry
   * @param {number} userId - User ID
   * @param {Object} foodData - Custom food data
   * @param {string} foodData.name - Food name
   * @param {number} foodData.serving_size - Serving size
   * @param {string} foodData.serving_unit - Serving unit (g, ml, oz, etc.)
   * @param {number} foodData.calories - Calories per serving
   * @param {number} foodData.protein - Protein in grams
   * @param {number} foodData.carbs - Carbs in grams
   * @param {number} foodData.fat - Fat in grams
   */
  createCustomFood(userId, foodData) {
    return HTTP.post(API_SERVER, {
      action: "createCustomFood",
      user_id: userId,
      ...foodData,
    });
  },

  /**
   * Get user's custom foods
   * @param {number} userId - User ID
   */
  getCustomFoods(userId) {
    return HTTP.post(API_SERVER, {
      action: "getCustomFoods",
      user_id: userId,
    });
  },

  // =========================================================================
  // MEAL INTEGRATION
  // =========================================================================

  /**
   * Quick log food from search/scan to nutrition log
   * @param {number} userId - User ID
   * @param {Object} foodData - Food data from search/scan
   * @param {string} mealType - Meal type (breakfast, lunch, etc.)
   * @param {string} date - Date YYYY-MM-DD
   * @param {number} servings - Number of servings
   */
  quickLogFood(userId, foodData, mealType, date, servings = 1) {
    return HTTP.post(API_SERVER, {
      action: "quickLogFood",
      user_id: userId,
      food_data: foodData,
      meal_type: mealType,
      date: date,
      servings: servings,
    });
  },

  // =========================================================================
  // UTILITY METHODS
  // =========================================================================

  /**
   * Get common serving sizes for a food type
   * @param {string} foodCategory - Food category
   */
  getServingSizes(foodCategory = null) {
    const commonServings = {
      liquid: [
        { amount: 100, unit: "ml", label: "100ml" },
        { amount: 200, unit: "ml", label: "Glass (200ml)" },
        { amount: 250, unit: "ml", label: "Cup (250ml)" },
        { amount: 330, unit: "ml", label: "Can (330ml)" },
        { amount: 500, unit: "ml", label: "Bottle (500ml)" },
      ],
      solid: [
        { amount: 1, unit: "piece", label: "1 piece" },
        { amount: 50, unit: "g", label: "50g" },
        { amount: 100, unit: "g", label: "100g" },
        { amount: 150, unit: "g", label: "150g" },
        { amount: 200, unit: "g", label: "200g" },
      ],
      grain: [
        { amount: 30, unit: "g", label: "30g (1 serving)" },
        { amount: 60, unit: "g", label: "60g (2 servings)" },
        { amount: 100, unit: "g", label: "100g" },
        { amount: 1, unit: "cup", label: "1 cup cooked" },
      ],
      protein: [
        { amount: 100, unit: "g", label: "100g" },
        { amount: 150, unit: "g", label: "150g (palm size)" },
        { amount: 200, unit: "g", label: "200g" },
        { amount: 1, unit: "breast", label: "1 chicken breast" },
      ],
    };

    if (foodCategory && commonServings[foodCategory]) {
      return commonServings[foodCategory];
    }
    return commonServings.solid;
  },

  /**
   * Format nutrition display
   * @param {Object} nutrition - Nutrition data
   */
  formatNutrition(nutrition) {
    return {
      calories: Math.round(nutrition.nf_calories || nutrition.calories || 0),
      protein: Math.round((nutrition.nf_protein || nutrition.protein || 0) * 10) / 10,
      carbs: Math.round((nutrition.nf_total_carbohydrate || nutrition.carbs || 0) * 10) / 10,
      fat: Math.round((nutrition.nf_total_fat || nutrition.fat || 0) * 10) / 10,
      fiber: Math.round((nutrition.nf_dietary_fiber || nutrition.fiber || 0) * 10) / 10,
      sugar: Math.round((nutrition.nf_sugars || nutrition.sugar || 0) * 10) / 10,
      sodium: Math.round(nutrition.nf_sodium || nutrition.sodium || 0),
      servingSize: nutrition.serving_qty || 1,
      servingUnit: nutrition.serving_unit || "serving",
    };
  },

  /**
   * Scale nutrition values by servings
   * @param {Object} nutrition - Base nutrition data
   * @param {number} servings - Number of servings
   */
  scaleByServings(nutrition, servings) {
    const scaled = {};
    const numericKeys = ["calories", "protein", "carbs", "fat", "fiber", "sugar", "sodium"];

    for (const key of numericKeys) {
      if (nutrition[key] !== undefined) {
        scaled[key] = Math.round(nutrition[key] * servings * 10) / 10;
      }
    }

    scaled.servings = servings;
    scaled.servingSize = nutrition.servingSize;
    scaled.servingUnit = nutrition.servingUnit;

    return scaled;
  },

  /**
   * Get macro breakdown percentages
   * @param {Object} nutrition - Nutrition data
   */
  getMacroBreakdown(nutrition) {
    const protein = nutrition.protein || 0;
    const carbs = nutrition.carbs || 0;
    const fat = nutrition.fat || 0;

    const proteinCal = protein * 4;
    const carbsCal = carbs * 4;
    const fatCal = fat * 9;
    const totalCal = proteinCal + carbsCal + fatCal;

    if (totalCal === 0) {
      return { protein: 0, carbs: 0, fat: 0 };
    }

    return {
      protein: Math.round((proteinCal / totalCal) * 100),
      carbs: Math.round((carbsCal / totalCal) * 100),
      fat: Math.round((fatCal / totalCal) * 100),
    };
  },

  /**
   * Get food category color
   * @param {string} category - Food category
   */
  getCategoryColor(category) {
    const colors = {
      protein: "#dc3545",
      carbohydrate: "#ffc107",
      vegetable: "#28a745",
      fruit: "#fd7e14",
      dairy: "#17a2b8",
      fat: "#6f42c1",
      beverage: "#007bff",
      snack: "#6c757d",
    };
    return colors[category?.toLowerCase()] || "#6c757d";
  },

  /**
   * Parse barcode from various formats
   * @param {string} input - Barcode input
   */
  parseBarcode(input) {
    // Remove any non-numeric characters
    const cleaned = input.replace(/\D/g, "");

    // UPC-A: 12 digits
    // EAN-13: 13 digits
    // UPC-E: 8 digits
    if (cleaned.length === 12 || cleaned.length === 13 || cleaned.length === 8) {
      return cleaned;
    }

    // Invalid barcode
    return null;
  },

  /**
   * Get athlete-specific food recommendations
   * @param {string} context - Training context (pre_training, post_training, match_day, etc.)
   */
  getAthleteRecommendations(context) {
    const recommendations = {
      pre_training: {
        title: "Pre-Training Fuel",
        description: "1-2 hours before training",
        focus: ["Complex carbs", "Moderate protein", "Low fat"],
        suggestions: ["Oatmeal with banana", "Whole grain toast with peanut butter", "Rice with chicken"],
      },
      post_training: {
        title: "Recovery Nutrition",
        description: "Within 60 minutes after training",
        focus: ["Fast carbs", "High protein", "Electrolytes"],
        suggestions: ["Protein shake with fruit", "Chocolate milk", "Greek yogurt with berries"],
      },
      pre_match: {
        title: "Pre-Match Meal",
        description: "3-4 hours before kickoff",
        focus: ["High carbs", "Moderate protein", "Low fiber", "Low fat"],
        suggestions: ["Pasta with tomato sauce", "Rice with grilled chicken", "Bagel with jam"],
      },
      post_match: {
        title: "Match Recovery",
        description: "Within 60 minutes after match",
        focus: ["Replenish glycogen", "Repair muscle", "Rehydrate"],
        suggestions: ["Recovery shake", "Chicken wrap", "Smoothie bowl"],
      },
      rest_day: {
        title: "Rest Day Nutrition",
        description: "Lower calorie day focused on recovery",
        focus: ["Lean protein", "Vegetables", "Healthy fats"],
        suggestions: ["Salad with grilled fish", "Vegetable stir-fry", "Eggs with avocado"],
      },
    };

    return recommendations[context] || recommendations.rest_day;
  },
};

export { FoodDatabase };
export default FoodDatabase;
